/**
 * Moneyness Utilities
 * Handles moneyness categorization on the frontend
 */

import { ScannerData } from '../types/ScannerData';

export type MoneynessCategory = 'ITM' | 'ATM' | 'OTM' | 'Unknown';

/**
 * Calculate moneyness category from numeric moneyness value and option type
 */
export const calculateMoneynessCategory = (
  moneyness: number | string | null | undefined,
  optionType: string | null | undefined
): MoneynessCategory => {
  try {
    const moneynessVal = typeof moneyness === 'string' ? parseFloat(moneyness) : Number(moneyness);
    const optionRaw = String(optionType || '').toUpperCase();
    const option = optionRaw === 'CALL' ? 'C' : optionRaw === 'PUT' ? 'P' : optionRaw;
    
    if (isNaN(moneynessVal) || !option) {
      return 'Unknown';
    }
    
    if (option === 'C') { // Call option
      if (moneynessVal > 0.5) { // Significantly in the money
        return 'ITM';
      } else if (Math.abs(moneynessVal) <= 0.5) { // Near the money
        return 'ATM';
      } else { // Out of the money
        return 'OTM';
      }
    } else if (option === 'P') { // Put option
      if (moneynessVal < -0.5) { // Significantly in the money
        return 'ITM';
      } else if (Math.abs(moneynessVal) <= 0.5) { // Near the money
        return 'ATM';
      } else { // Out of the money
        return 'OTM';
      }
    } else {
      return 'Unknown';
    }
  } catch (error) {
    return 'Unknown';
  }
};

/**
 * Add moneyness categories to scanner data
 */
export const addMoneynessCategories = (data: ScannerData[]): ScannerData[] => {
  return data.map(item => ({
    ...item,
    MoneynessCategory: calculateMoneynessCategory(item.Moneyness, item['Option Type'])
  }));
};

/**
 * Get available moneyness categories from data
 */
export const getAvailableMoneynessCategories = (data: ScannerData[]): MoneynessCategory[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }
  
  const categories = new Set<MoneynessCategory>();
  
  data.forEach(item => {
    const category = calculateMoneynessCategory(item.Moneyness, item['Option Type']);
    categories.add(category);
  });
  
  return Array.from(categories).sort();
};

/**
 * Filter data by moneyness category
 */
export const filterByMoneynessCategory = (
  data: ScannerData[],
  selectedCategory: MoneynessCategory | 'all'
): ScannerData[] => {
  if (selectedCategory === 'all') {
    return data;
  }
  
  return data.filter(item => {
    const category = calculateMoneynessCategory(item.Moneyness, item['Option Type']);
    return category === selectedCategory;
  });
}; 